import React from "react"

// Translation system utility functions
// These are injected at build time via webpack DefinePlugin
/* global __TRANSLATIONS__, __LANGUAGE__ */
let translations = typeof __TRANSLATIONS__ !== 'undefined' ? __TRANSLATIONS__ : {}
let currentLanguage = typeof __LANGUAGE__ !== 'undefined' ? __LANGUAGE__ : 'en'

/**
 * Initialize translations with the provided language data
 * This is called during Gatsby build process
 */
export const initializeTranslations = (translationData, language) => {
  translations = translationData
  currentLanguage = language
}

/**
 * Get current language
 */
export const getCurrentLanguage = () => currentLanguage

/**
 * Get translation for a given key
 * Uses flat key structure (e.g., 'forms.contact.name')
 */
export const getTranslation = (key, fallback = key) => {
  if (!key) return fallback

  if (translations && key in translations) {
    return translations[key]
  } else {
    // If key not found, return fallback or the key itself
    console.warn(`Translation key "${key}" not found for language "${currentLanguage}"`)
    return fallback
  }
}

/**
 * Translation function with interpolation support
 * Usage: t('welcome.message', { name: 'John' }) -> "Welcome, John!"
 */
export const t = (key, variables = {}, fallback = key) => {
  let translation = getTranslation(key, fallback)

  // Handle variable interpolation
  if (variables && typeof variables === 'object') {
    Object.keys(variables).forEach(variable => {
      const regex = new RegExp(`{{\\s*${variable}\\s*}}`, 'g')
      translation = translation.replace(regex, variables[variable])
    })
  }

  return translation
}

/**
 * Format date string based on current language
 * @param {string|Date} date - Date to format
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date string
 */
export const formatDate = (date, options = {}) => {
  if (!date) return ''
  
  const dateObj = typeof date === 'string' ? new Date(date) : date
  
  // Default formatting options per language
  const defaultOptions = {
    en: {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    },
    ja: {
      year: 'numeric',
      month: 'long', 
      day: 'numeric'
    }
  }
  
  // Language-specific locale codes
  const locales = {
    en: 'en-GB',
    ja: 'ja-JP'
  }
  
  const locale = locales[currentLanguage] || locales.en
  const formatOptions = { ...defaultOptions[currentLanguage] || defaultOptions.en, ...options }
  
  try {
    return dateObj.toLocaleDateString(locale, formatOptions)
  } catch (error) {
    console.warn(`Error formatting date for language "${currentLanguage}":`, error)
    // Fallback to English formatting
    return dateObj.toLocaleDateString('en-GB', defaultOptions.en)
  }
}

/**
 * React hook for translations
 * This provides access to the translation function in React components
 */
export const useTranslations = () => {
  return {
    t,
    formatDate,
    language: currentLanguage,
    getCurrentLanguage,
  }
}

/**
 * Higher-order component to provide translations context
 */
export const withTranslations = (WrappedComponent) => {
  return (props) => {
    const translationProps = useTranslations()
    return <WrappedComponent {...props} {...translationProps} />
  }
}

export default {
  t,
  formatDate,
  getTranslation,
  getCurrentLanguage,
  initializeTranslations,
  useTranslations,
  withTranslations,
}
